import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormError } from "../../../components/FormError";

interface PromotionFormData {
  packageId: string;
  price: string;
  startDate: string;
  endDate: string;
}

const schema = yup.object().shape({
  packageId: yup.string().required("Package selection is required"),
  price: yup
    .string()
    .required("Price is required")
    .matches(/^\d+(\.\d{1,2})?$/, "Invalid price format"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup
    .string()
    .required("End date is required")
    .test("date", "End date must be after start date", function (value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return true;
      return new Date(value) > new Date(startDate);
    }),
});

export const PromotionalPricingPage: React.FC = () => {
  const [successMessage, setSuccessMessage] = React.useState("");
  const [packages, setPackages] = React.useState([
    { id: "1", name: "Basic English" },
    { id: "2", name: "Intermediate English" },
    { id: "3", name: "Advanced English" },
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PromotionFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: PromotionFormData) => {
    try {
      // Implementar chamada API para criar promoção
      console.log("Promotion data:", data);
      setSuccessMessage("Promotion created successfully");
    } catch (error) {
      console.error("Error creating promotion:", error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Create Promotional Price</h1>

      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label
              htmlFor="package-select"
              className="block text-sm font-medium text-gray-700"
            >
              Select Package
            </label>
            <select
              id="package-select"
              {...register("packageId")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            >
              <option value="">Select a package</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </option>
              ))}
            </select>
            <FormError message={errors.packageId?.message} />
          </div>

          <div>
            <label
              htmlFor="price-input"
              className="block text-sm font-medium text-gray-700"
            >
              Promotional Price
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                id="price-input"
                type="text"
                {...register("price")}
                className="pl-7 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="0.00"
              />
            </div>
            <FormError message={errors.price?.message} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="start-date"
                className="block text-sm font-medium text-gray-700"
              >
                Start Date
              </label>
              <input
                id="start-date"
                type="date"
                {...register("startDate")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
              <FormError message={errors.startDate?.message} />
            </div>

            <div>
              <label
                htmlFor="end-date"
                className="block text-sm font-medium text-gray-700"
              >
                End Date
              </label>
              <input
                id="end-date"
                type="date"
                {...register("endDate")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
              <FormError message={errors.endDate?.message} />
            </div>
          </div>

          <button
            id="save-promotion"
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Create Promotion
          </button>
        </form>

        {successMessage && (
          <div
            id="success-message"
            className="mt-4 p-4 bg-green-100 text-green-700 rounded-md"
          >
            {successMessage}
          </div>
        )}
      </div>
    </div>
  );
};
